import React, { useState } from "react";
import { formatOrderType, formatOrderedTime } from "../utils/orderUtils";
import { TABLE_ROWS_PER_PAGE } from "../utils/constants";
import Pagination from "../../Pagination";
import { useRestaurantConfig } from "../../../utils/restaurantConfig";

const OrdersTable = ({
  orders,
  currencySymbol,
  showAggregatedView,
  onToggleView,
  onViewOrder,
  isLoading
}) => {
  const { timezone } = useRestaurantConfig();
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(1); // 1 for asc, -1 for desc
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  // const [viewOrder, setViewOrder] = useState(null);

  // Pagination calculations
  const indexOfLastOrder = currentPage * TABLE_ROWS_PER_PAGE;
  const indexOfFirstOrder = indexOfLastOrder - TABLE_ROWS_PER_PAGE;

  // Sort orders
  const sortedOrders = [...orders].sort((a, b) => {
    if (!sortField) return 0;

    const aValue = sortField === "amount" ? Number(a[sortField]) : a[sortField];
    const bValue = sortField === "amount" ? Number(b[sortField]) : b[sortField];

    if (aValue < bValue) return -1 * sortOrder;
    if (aValue > bValue) return 1 * sortOrder;
    return 0;
  });

  const currentOrders = sortedOrders.slice(indexOfFirstOrder, indexOfLastOrder);
  const totalPages = Math.ceil(orders.length / TABLE_ROWS_PER_PAGE);

  // Reset to page 1 when orders change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [orders.length]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder * -1);
    } else {
      setSortField(field);
      setSortOrder(1);
    }
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      pending: { bg: "bg-amber-100", text: "text-amber-600", icon: "⏱️" },
      confirmed: { bg: "bg-sky-100", text: "text-sky-600", icon: "✓" },
      preparing: { bg: "bg-purple-100", text: "text-purple-600", icon: "👨‍🍳" },
      ready: { bg: "bg-blue-100", text: "text-blue-600", icon: "✓" },
      delivered: { bg: "bg-green-100", text: "text-green-600", icon: "✓" },
      completed: { bg: "bg-green-100", text: "text-green-600", icon: "✓" },
      cancelled: { bg: "bg-red-100", text: "text-red-600", icon: "✕" },
    };

    const style = statusStyles[status] || {
      bg: "bg-gray-100",
      text: "text-gray-600",
      icon: "•",
    };

    return (
      <span
        className={`${style.bg} ${style.text} px-3 py-1.5 rounded-full text-xs font-medium inline-flex items-center gap-1.5`}
      >
        <span>{style.icon}</span>
        <span className="capitalize">{status}</span>
      </span>
    );
  };

  const getItems = (rowData) => {
    const products = (rowData.products || [])
      .filter((p) => p.active)
      .map((p) => ({
        name: p.productTitle?.split(",")[0] || "Unnamed",
        qty: p.quantity,
      }));

    const displayProducts = products.slice(0, 2);
    const remaining = products.length - 2;
    const isExpanded = expandedOrderId === rowData._id;

    return (
      <div className="text-sm text-gray-600 leading-tight">
        {(isExpanded ? products : displayProducts).map((p, idx) => (
          <div key={idx}>
            {p.name}{" "}
            <span className="font-semibold text-gray-700">×{p.qty}</span>
          </div>
        ))}
        {remaining > 0 && (
          <button
            className="text-xs text-blue-600 font-semibold hover:text-blue-800 underline mt-1 transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              setExpandedOrderId(isExpanded ? null : rowData._id);
            }}
          >
            {isExpanded ? "Show less" : `+${remaining} more`}
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
      {/* Modern Table Header */}
      <div className="bg-gradient-to-r from-gray-900 to-black px-6 py-4 flex items-center justify-between border-b-2 border-white">
        <h3 className="text-white text-lg font-semibold">Order History</h3>
        {onToggleView && (
          <button
            onClick={onToggleView}
            className="flex items-center gap-2 px-3 py-1.5 bg-white/10 hover:bg-white/20 border border-white/30 rounded-md transition-all duration-200 text-white text-xs font-medium"
            title={
              showAggregatedView
                ? "Show Detailed Table"
                : "Show Order Type Summary"
            }
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              />
            </svg>
            {showAggregatedView ? "Show Details" : "Type Summary"}
          </button>
        )}
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="px-6 py-16 text-center">
          <div className="animate-pulse flex flex-col items-center justify-center">
            <svg className="w-12 h-12 text-gray-300 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <p className="text-gray-500 font-medium text-lg">Loading orders...</p>
          </div>
        </div>
      ) : currentOrders.length === 0 ? (
        <div className="px-6 py-16 text-center">
          <div className="text-gray-400 mb-3">
            <svg
              className="w-16 h-16 mx-auto"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
              />
            </svg>
          </div>
          <p className="text-gray-500 text-lg font-medium">
            No orders available
          </p>
          <p className="text-gray-400 text-sm mt-1">
            Try adjusting your filters
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-gray-900 to-black sticky top-0 z-10">
              <tr>
                <th
                  className="px-6 py-5 text-left text-sm font-bold text-white uppercase tracking-wider cursor-pointer hover:bg-gray-800"
                  onClick={() => handleSort("orderId")}
                >
                  <div className="flex items-center gap-2">
                    Order
                    {sortField === "orderId" && (
                      <span className="text-xs">
                        {sortOrder === 1 ? "▲" : "▼"}
                      </span>
                    )}
                  </div>
                </th>
                <th className="px-6 py-5 text-center text-sm font-bold text-white uppercase tracking-wider">
                  Bill No
                </th>

                <th
                  className="px-6 py-5 text-left text-sm font-bold text-white uppercase tracking-wider cursor-pointer hover:bg-gray-800"
                  onClick={() => handleSort("orderType")}
                >
                  <div className="flex items-center gap-2">
                    Type
                    {sortField === "orderType" && (
                      <span className="text-xs">
                        {sortOrder === 1 ? "▲" : "▼"}
                      </span>
                    )}
                  </div>
                </th>
                <th className="px-6 py-5 text-left text-sm font-bold text-white uppercase tracking-wider">
                  Contact
                </th>
                <th
                  className="px-6 py-5 text-left text-sm font-bold text-white uppercase tracking-wider cursor-pointer hover:bg-gray-800"
                  onClick={() => handleSort("amount")}
                >
                  <div className="flex items-center gap-2">
                    Amount
                    {sortField === "amount" && (
                      <span className="text-xs">
                        {sortOrder === 1 ? "▲" : "▼"}
                      </span>
                    )}
                  </div>
                </th>
                <th className="px-6 py-5 text-left text-sm font-bold text-white uppercase tracking-wider">
                  Items
                </th>
                <th className="px-6 py-5 text-left text-sm font-bold text-white uppercase tracking-wider">
                  Driver
                </th>
                <th
                  className="px-6 py-5 text-left text-sm font-bold text-white uppercase tracking-wider cursor-pointer hover:bg-gray-800"
                  onClick={() => handleSort("status")}
                >
                  <div className="flex items-center gap-2">
                    Status
                    {sortField === "status" && (
                      <span className="text-xs">
                        {sortOrder === 1 ? "▲" : "▼"}
                      </span>
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-5 text-left text-sm font-bold text-white uppercase tracking-wider cursor-pointer hover:bg-gray-800"
                  onClick={() => handleSort("createdAt")}
                >
                  <div className="flex items-center gap-2">
                    Time
                    {sortField === "createdAt" && (
                      <span className="text-xs">
                        {sortOrder === 1 ? "▲" : "▼"}
                      </span>
                    )}
                  </div>
                </th>
                <th className="px-6 py-5 text-center text-sm font-bold text-white uppercase tracking-wider">
                  View
                </th>

              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {currentOrders.map((order, index) => {
                const driverName =
                  order.status === "pending"
                    ? "Not Assigned"
                    : order.assignedDriver?.name ||
                    order.driverName ||
                    "Not Assigned";

                return (
                  <tr
                    key={order.orderId || index}
                    className="h-[70px] hover:bg-gradient-to-r hover:from-gray-50 hover:to-white transition-all duration-200 hover:shadow-sm"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="font-semibold text-gray-900 text-base">
                        #{order.orderId}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-700">
                      {order.billNumber || "-"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-gray-700 text-sm capitalize">
                        {formatOrderType(order.orderType)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-gray-600 text-sm font-medium">
                        {order.phoneNumber}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="font-semibold text-green-600 text-base">
                        {currencySymbol}
                        {Number(order.amount || 0).toFixed(2)}
                      </span>
                    </td>
                    <td className="px-6 py-4">{getItems(order)}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`text-sm font-medium ${driverName === "Not Assigned"
                            ? "text-gray-400"
                            : "text-gray-700"
                          }`}
                      >
                        {driverName}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(order.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-600 font-medium">
                        {formatOrderedTime(order.createdAt, timezone)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <button
                        onClick={() => onViewOrder && onViewOrder(order)}
                        className="p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200"
                        title="View Order"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5 text-gray-600 hover:text-blue-600"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M2.458 12C3.732 7.943 7.523 5 12 5
           c4.477 0 8.268 2.943 9.542 7
           -1.274 4.057-5.065 7-9.542 7
           -4.477 0-8.268-2.943-9.542-7z"
                          />
                        </svg>
                      </button>
                    </td>

                  </tr>

                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Custom Pagination */}
      {orders.length > TABLE_ROWS_PER_PAGE && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      )}
    </div>
  );
};

export default OrdersTable;
